/**
 * BrandFormatModeToggle — Peace <-> SPICE brand selector for the Brand
 * Formatter (Phase C2).
 *
 * Mirrors EmailModeToggle's slider (a soft pill with a knob that glides
 * between two labels), but this is a BRAND-CONTEXT switch, not a voice switch:
 * the mechanical-cleanup formatting is identical in both modes; only the
 * downloaded .docx letterhead + colors differ (Peace branding vs the SPICE
 * lockup). Peace is sage (the default Peace tool color); SPICE carries the
 * pepper accent + the SPICE pepper glyph, consistent with the email/assessment
 * SPICE treatment.
 *
 * Accessibility: a single role="switch" button (off = Peace, on = SPICE),
 * keyboard-operable natively, with a dynamic aria-label naming the active
 * brand. Honors prefers-reduced-motion.
 */

import { type ReactNode, useMemo } from 'react';
import { SPICE_PEPPER_SVG } from '@/lib/hubLogos';

export type BrandFormatMode = 'peace' | 'spice';

export interface BrandFormatModeToggleProps {
  mode: BrandFormatMode;
  onChange: (mode: BrandFormatMode) => void;
  disabled?: boolean;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Sage-600 default Peace primary for the Peace knob (matches EmailModeToggle).
const SAGE_600 = 'var(--color-sage-600)';

export function BrandFormatModeToggle({
  mode,
  onChange,
  disabled = false,
}: BrandFormatModeToggleProps): ReactNode {
  const isSpice = mode === 'spice';
  const reduceMotion = useMemo(prefersReducedMotion, []);

  return (
    <div>
      <span className="block text-sm font-medium text-ink mb-1">Brand</span>
      <button
        type="button"
        role="switch"
        aria-checked={isSpice}
        aria-label={`Document brand: ${isSpice ? 'SPICE' : 'Peace'}. Switch to ${isSpice ? 'Peace' : 'SPICE'}.`}
        disabled={disabled}
        onClick={() => onChange(isSpice ? 'peace' : 'spice')}
        data-testid="brand-format-mode-toggle"
        className="relative grid grid-cols-2 w-full max-w-sm min-h-11 p-1 rounded-full bg-sand border border-sage-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 disabled:opacity-50"
      >
        <span
          aria-hidden="true"
          className={
            'absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] rounded-full shadow-sm' +
            (reduceMotion ? '' : ' transition-transform duration-200 ease-out')
          }
          style={{
            transform: isSpice ? 'translateX(100%)' : 'translateX(0)',
            backgroundColor: isSpice ? 'var(--color-pepper)' : SAGE_600,
          }}
        />
        <span
          className={
            'relative z-10 flex items-center justify-center gap-1 px-3 text-sm font-semibold rounded-full ' +
            (isSpice ? 'text-ink-soft' : 'text-white')
          }
        >
          Peace
        </span>
        <span
          className={
            'relative z-10 flex items-center justify-center gap-1.5 px-3 text-sm font-semibold rounded-full ' +
            (isSpice ? 'text-white' : 'text-ink-soft')
          }
        >
          <span
            aria-hidden="true"
            className="inline-block h-3.5 [&>svg]:h-full [&>svg]:w-auto"
            dangerouslySetInnerHTML={{ __html: SPICE_PEPPER_SVG }}
          />
          SPICE
        </span>
      </button>
    </div>
  );
}
